import Phaser from "phaser";
import { COLORS } from "../constants";
import {
  RUN_BANNER_HIDE_EVENT,
  RUN_BANNER_SHOW_EVENT,
  type RunBannerConfig,
} from "../scenes/RunHudScene";

type TransitionOptions = {
  keepBanner?: boolean;
  duration?: number;
};

const DEFAULT_FADE_DURATION = 220;

export class OverlayManager {
  private readonly scene: Phaser.Scene;
  private readonly overlayLayer: Phaser.GameObjects.Container;
  private readonly fadeRect: Phaser.GameObjects.Rectangle;

  private transitionTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene, overlayLayer: Phaser.GameObjects.Container) {
    this.scene = scene;
    this.overlayLayer = overlayLayer;

    const { width, height } = scene.scale;
    this.fadeRect = scene.add
      .rectangle(0, 0, width, height, COLORS.background, 1)
      .setOrigin(0, 0)
      .setAlpha(0)
      .disableInteractive();
    this.overlayLayer.add(this.fadeRect);
  }

  showBanner(config: RunBannerConfig) {
    this.scene.game.events.emit(RUN_BANNER_SHOW_EVENT, config);
  }

  hideBanner() {
    this.scene.game.events.emit(RUN_BANNER_HIDE_EVENT);
  }

  transition(doSwap: () => void, options?: TransitionOptions) {
    const duration = options?.duration ?? DEFAULT_FADE_DURATION;

    // Ensure no stale tween/interactive overlay can block input.
    this.transitionTween?.stop();
    this.scene.tweens.killTweensOf(this.fadeRect);
    this.fadeRect.disableInteractive();
    this.fadeRect.setAlpha(0);
    this.fadeRect.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, this.fadeRect.width, this.fadeRect.height),
      Phaser.Geom.Rectangle.Contains
    );

    this.transitionTween = this.scene.tweens.add({
      targets: this.fadeRect,
      alpha: 1,
      duration,
      ease: "Sine.easeInOut",
      onComplete: () => {
        doSwap();
        // Unblock input immediately after swap; the fade-out is cosmetic.
        // This also protects against edge cases where the scene changes before
        // the fade-out tween completes.
        this.fadeRect.disableInteractive();
        this.scene.tweens.add({
          targets: this.fadeRect,
          alpha: 0,
          duration,
          ease: "Sine.easeInOut",
          onComplete: () => {
            if (!options?.keepBanner) {
              this.hideBanner();
            }
            this.fadeRect.disableInteractive();
          },
        });
      },
    });
  }
}
